import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Loading, useDataProvider, useNotify } from "react-admin";

const SETTINGS_RESOURCE = "settings";
const SETTING_NAME = "firebase_notification_filter";

// registration_date_started_days is stored as a [maxDaysAgo, minDaysAgo].
const REGISTRATION_DAYS_KEY = "registration_date_started_days";
const DESCRIPTION_KEY = "description";

const FIELD_LABELS: Record<string, string> = {
  notification_name: "Notification Name",
  description: "Description",
  app_id: "App ID",
  days_since_logged_in_min: "Days Since Logged In (min)",
  days_since_logged_in_max: "Days Since Logged In (max)",
  country: "Country",
  language: "Language",
};

const labelFor = (key: string) =>
  FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

// The setting is written by the backend with ast.literal_eval, so it is a Python dict literal.
const parsePythonLiteral = (raw: unknown): any => {
  if (raw === null || typeof raw !== "string") {
    return raw;
  }

  const normalized = raw
    .trim()
    .replace(/'/g, '"')
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/\bNone\b/g, "null");

  try {
    return JSON.parse(normalized);
  } catch (error) {
    try {
      return JSON.parse(raw);
    } catch (nestedError) {
      return null;
    }
  }
};

// ast.literal_eval cannot read JSON's true/false/null, so write Python literals back.
const toPythonLiteral = (value: any): string =>
  JSON.stringify(value, null, 2)
    .replace(/\btrue\b/g, "True")
    .replace(/\bfalse\b/g, "False")
    .replace(/\bnull\b/g, "None");

const describeCriteria = (entry: Record<string, any>): string => {
  const registrationDays = entry[REGISTRATION_DAYS_KEY];
  const parts: string[] = [];

  if (Array.isArray(registrationDays)) {
    parts.push(`Registered between min. ${registrationDays[1] ?? "?"} and max. ${registrationDays[0] ?? "?"} days`);
  }

  Object.entries(entry).forEach(([key, value]) => {
    if (key === REGISTRATION_DAYS_KEY || key === "notification_name" || key === "app_id" || key === DESCRIPTION_KEY) {
      return;
    }
    if (value === null || value === undefined || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      parts.push(`${labelFor(key)}: ${value.join(", ")}`);
      return;
    }
    if (typeof value === "object") {
      parts.push(`${labelFor(key)}: ${JSON.stringify(value)}`);
      return;
    }
    parts.push(`${labelFor(key)}: ${String(value)}`);
  });

  return parts.join(" · ");
};

const useNotificationFilterSetting = () => {
  const dataProvider = useDataProvider();
  const [record, setRecord] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    dataProvider
      .getList(SETTINGS_RESOURCE, {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "createdAt", order: "DESC" },
        filter: { name: SETTING_NAME },
      })
      .then((result: any) => {
        if (mounted) setRecord(result.data?.[0] ?? null);
      })
      .catch(() => {
        if (mounted) setRecord(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [dataProvider, reloadToken]);

  const config = useMemo(() => parsePythonLiteral(record?.value) ?? {}, [record]);
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { record, config, loading, reload };
};

export const NotificationSettingsList = () => {
  const { config, loading } = useNotificationFilterSetting();

  if (loading) {
    return <Loading />;
  }

  const entries = Object.entries(config as Record<string, any>);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Notification Settings
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Task Key</TableCell>
              <TableCell>Notification Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>App ID</TableCell>
              <TableCell>Criteria</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, entry]) => (
              <TableRow key={key}>
                <TableCell>{key}</TableCell>
                <TableCell>{entry?.notification_name ?? ""}</TableCell>
                <TableCell>{entry?.[DESCRIPTION_KEY] ?? ""}</TableCell>
                <TableCell>{entry?.app_id ?? ""}</TableCell>
                <TableCell>{describeCriteria(entry || {})}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      component={RouterLink}
                      to={`/notification-settings/${encodeURIComponent(key)}/show`}
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon fontSize="small" />}
                    >
                      Show
                    </Button>
                    <Button
                      component={RouterLink}
                      to={`/notification-settings/${encodeURIComponent(key)}/edit`}
                      size="small"
                      variant="contained"
                      startIcon={<EditIcon fontSize="small" />}
                    >
                      Edit
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RegistrationDaysFields: React.FC<{
  value: any[];
  readOnly: boolean;
  onChange: (next: any[]) => void;
}> = ({ value, readOnly, onChange }) => {
  const maxDays = value?.[0] ?? "";
  const minDays = value?.[1] ?? "";

  const update = (index: number, raw: string) => {
    const next = [value?.[0] ?? null, value?.[1] ?? null];
    next[index] = raw === "" ? null : Number(raw);
    onChange(next);
  };

  if (readOnly) {
    return (
      <Typography>
        Registered between min. {minDays} and max. {maxDays} days
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={2}>
      <MuiTextField
        size="small"
        type="number"
        label="Registered min. days ago"
        value={minDays}
        onChange={(event) => update(1, event.target.value)}
      />
      <MuiTextField
        size="small"
        type="number"
        label="Registered max. days ago"
        value={maxDays}
        onChange={(event) => update(0, event.target.value)}
      />
    </Stack>
  );
};

const coerceScalar = (raw: string, previous: any) => {
  if (raw === "") {
    return typeof previous === "number" ? null : "";
  }
  if (typeof previous === "number") {
    return Number(raw);
  }
  // Keep numeric-looking values numeric so criteria stay comparable server-side.
  if (previous === null && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return raw;
};

const ListValueField: React.FC<{
  label: string;
  value: any[];
  readOnly: boolean;
  onChange: (next: any[]) => void;
}> = ({ label, value, readOnly, onChange }) => {
  if (readOnly) {
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography>{value.map((item) => String(item ?? "")).join(", ")}</Typography>
      </>
    );
  }

  const updateItem = (index: number, raw: string) => {
    const next = [...value];
    next[index] = coerceScalar(raw, value[index]);
    onChange(next);
  };

  return (
    <>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {value.map((item, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <MuiTextField
              fullWidth
              size="small"
              type={typeof item === "number" ? "number" : "text"}
              value={item ?? ""}
              onChange={(event) => updateItem(index, event.target.value)}
            />
            <IconButton
              size="small"
              color="error"
              aria-label={`Remove item ${index + 1}`}
              onClick={() => onChange(value.filter((_item, itemIndex) => itemIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={() => onChange([...value, typeof value[0] === "number" ? 0 : ""])}
        >
          Add Value
        </Button>
      </Stack>
    </>
  );
};

// Criteria are free-form, so each value is rendered by its runtime type.
const CriteriaField: React.FC<{
  fieldKey: string;
  value: any;
  readOnly: boolean;
  onChange: (next: any) => void;
}> = ({ fieldKey, value, readOnly, onChange }) => {
  const label = labelFor(fieldKey);

  if (fieldKey === DESCRIPTION_KEY) {
    const text = typeof value === "string" ? value : "";
    if (readOnly) {
      if (!text.trim()) {
        return null;
      }
      return (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{text}</Typography>
        </>
      );
    }
    return (
      <MuiTextField
        fullWidth
        multiline
        minRows={2}
        maxRows={8}
        size="small"
        label={label}
        placeholder="What does this notification do?"
        value={text}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (fieldKey === REGISTRATION_DAYS_KEY && Array.isArray(value)) {
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Registration Window
        </Typography>
        <RegistrationDaysFields value={value} readOnly={readOnly} onChange={onChange} />
      </>
    );
  }

  if (Array.isArray(value)) {
    return <ListValueField label={label} value={value} readOnly={readOnly} onChange={onChange} />;
  }

  if (typeof value === "boolean") {
    if (readOnly) {
      return (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
          <Typography>{value ? "Yes" : "No"}</Typography>
        </>
      );
    }
    return (
      <FormControlLabel
        control={<Switch size="small" checked={value} onChange={(event) => onChange(event.target.checked)} />}
        label={label}
      />
    );
  }

  if (value !== null && typeof value === "object") {
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Box sx={{ pl: 2, borderLeft: "2px solid", borderColor: "divider" }}>
          <NotificationSettingFields
            entry={value as Record<string, any>}
            readOnly={readOnly}
            onChange={(nestedKey, nestedValue) => onChange({ ...value, [nestedKey]: nestedValue })}
          />
        </Box>
      </>
    );
  }

  if (readOnly) {
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography>{value === null || value === undefined ? "" : String(value)}</Typography>
      </>
    );
  }

  return (
    <MuiTextField
      fullWidth
      size="small"
      label={label}
      type={typeof value === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(event) => onChange(coerceScalar(event.target.value, value))}
    />
  );
};

const NotificationSettingFields: React.FC<{
  entry: Record<string, any>;
  readOnly: boolean;
  onChange?: (key: string, value: any) => void;
  onDeleteField?: (key: string) => void;
}> = ({ entry, readOnly, onChange, onDeleteField }) => (
  <Stack spacing={2} sx={{ maxWidth: 560 }}>
    {Object.entries(entry)
      .filter(([key, value]) => !(readOnly && key === DESCRIPTION_KEY && !String(value ?? "").trim()))
      .map(([key, value]) => (
        <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <CriteriaField
              fieldKey={key}
              value={value}
              readOnly={readOnly}
              onChange={(next) => onChange?.(key, next)}
            />
          </Box>
          {!readOnly && onDeleteField && key !== DESCRIPTION_KEY && (
            <IconButton size="small" color="error" aria-label={`Delete ${key}`} onClick={() => onDeleteField(key)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ))}
  </Stack>
);

const AddCriteriaDialog: React.FC<{
  open: boolean;
  existingKeys: string[];
  onClose: () => void;
  onAdd: (key: string, value: any) => void;
}> = ({ open, existingKeys, onClose, onAdd }) => {
  const [fieldKey, setFieldKey] = useState("");
  const [valueType, setValueType] = useState<"text" | "number" | "list" | "boolean">("text");

  useEffect(() => {
    if (open) {
      setFieldKey("");
      setValueType("text");
    }
  }, [open]);

  const trimmed = fieldKey.trim();
  const isDuplicate = existingKeys.includes(trimmed);

  const defaultValueFor = () => {
    switch (valueType) {
      case "number":
        return 0;
      case "list":
        return [];
      case "boolean":
        return true;
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add criteria</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <MuiTextField
            autoFocus
            fullWidth
            size="small"
            label="Criteria key"
            placeholder="e.g. days_since_logged_in_max"
            value={fieldKey}
            error={isDuplicate}
            helperText={isDuplicate ? "This criteria already exists" : " "}
            onChange={(event) => setFieldKey(event.target.value)}
          />
          <MuiTextField
            select
            fullWidth
            size="small"
            label="Value type"
            value={valueType}
            onChange={(event) => setValueType(event.target.value as typeof valueType)}
          >
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="number">Number</MenuItem>
            <MenuItem value="list">List</MenuItem>
            <MenuItem value="boolean">Yes / No</MenuItem>
          </MuiTextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<CloseIcon fontSize="small" />} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon fontSize="small" />}
          disabled={!trimmed || isDuplicate}
          onClick={() => onAdd(trimmed, defaultValueFor())}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const NotificationSettingsShow = () => {
  const { key = "" } = useParams<{ key: string }>();
  const { config, loading } = useNotificationFilterSetting();

  if (loading) {
    return <Loading />;
  }

  const entry = (config as Record<string, any>)[key];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {key}
      </Typography>
      {entry ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <NotificationSettingFields entry={entry} readOnly />
        </Paper>
      ) : (
        <Typography color="text.secondary">Setting not found</Typography>
      )}
    </Box>
  );
};

export const NotificationSettingsEdit = () => {
  const { key = "" } = useParams<{ key: string }>();
  const { record, config, loading } = useNotificationFilterSetting();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<Record<string, any> | null>(null);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const current = (config as Record<string, any>)[key];
    // Always expose a description field so it can be filled in even when absent from the stored JSON.
    setEntry(current ? { [DESCRIPTION_KEY]: "", ...current } : null);
  }, [config, key]);

  if (loading) {
    return <Loading />;
  }

  if (!record || !entry) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Setting not found</Typography>
      </Box>
    );
  }

  const handleFieldChange = (fieldKey: string, value: any) => {
    setEntry((prev) => ({ ...(prev || {}), [fieldKey]: value }));
  };

  const handleFieldDelete = (fieldKey: string) => {
    setEntry((prev) => {
      const { [fieldKey]: removed, ...rest } = prev || {};
      return rest;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { [DESCRIPTION_KEY]: description, ...restEntry } = entry;
      const cleanedEntry = String(description ?? "").trim()
        ? { ...restEntry, [DESCRIPTION_KEY]: description }
        : restEntry;
      const nextConfig = { ...(config as Record<string, any>), [key]: cleanedEntry };
      await dataProvider.update(SETTINGS_RESOURCE, {
        id: record.id,
        data: { name: record.name, value: toPythonLiteral(nextConfig) },
        previousData: record,
      });
      notify("Notification settings updated", { type: "success" });
      navigate("/notification-settings");
    } catch (error) {
      notify("Failed to update notification settings", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {key}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <NotificationSettingFields
          entry={entry}
          readOnly={false}
          onChange={handleFieldChange}
          onDeleteField={handleFieldDelete}
        />
        <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
          Add Criteria
        </Button>
      </Paper>

      <AddCriteriaDialog
        open={addOpen}
        existingKeys={Object.keys(entry)}
        onClose={() => setAddOpen(false)}
        onAdd={(fieldKey, value) => {
          handleFieldChange(fieldKey, value);
          setAddOpen(false);
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Update"}
        </Button>
        <Button startIcon={<CloseIcon />} onClick={() => navigate("/notification-settings")}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};
